import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { studioService } from '../../utils/studioService';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import StudioSidebar from '../../components/ui/StudioSidebar';

const StudioDashboard = () => {
  const { user, session, userProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [studio, setStudio] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    games: [],
    analytics: {
      totalPlays: 0,
      totalRevenue: 0,
      activeGames: 0,
      monthlyGrowth: 0
    },
    recentActivity: []
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/user-login');
      return;
    }

    // Allow both studio_admin and admin roles
    if (userProfile?.role !== 'studio_admin') {
      navigate('/');
      return;
    }

    loadStudioData();
  }, [user, userProfile, navigate]);

  const loadStudioData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get studio info (with email fallback for manually linked studios)
      const { data: studioData, error: studioError } = await studioService.getStudioForUser(user.id);

      if (studioError) {
        setError('Failed to load studio data');
        return;
      }

      if (!studioData) {
        // No studio found - show helpful message
        setError('No studio linked to your account. Please contact support or wait for admin to set up your studio.');
        return;
      }

      setStudio(studioData);

      // Load dashboard analytics
      const dashboardResponse = await fetch('/api/studio/dashboard', {
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (dashboardResponse.ok) {
        const dashboardInfo = await dashboardResponse.json();
        setDashboardData(dashboardInfo);
      }

    } catch (err) {
      setError('Failed to load studio dashboard');
      console.error('Studio dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading studio dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">Error Loading Dashboard</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={loadStudioData}>Try Again</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <StudioSidebar />

      <div className="lg:pl-72">
        <div className="sticky top-0 z-40 bg-background/95 backdrop-blur-md border-b border-border lg:hidden">
          <div className="px-4 py-4">
            <h1 className="text-xl font-bold text-foreground">Studio Dashboard</h1>
          </div>
        </div>

        <main className="p-4 lg:p-8 pt-20 lg:pt-8 pb-20 lg:pb-8">
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">
                Welcome back, {studio?.name}!
              </h1>
              <p className="text-muted-foreground">
                Here's what's happening with your games today
              </p>
            </div>

            <div className="flex items-center space-x-3 mt-4 lg:mt-0">
              <Button
                variant="outline"
                size="sm"
                iconName="Plus"
                onClick={() => navigate('/studio-games')}
              >
                New Game
              </Button>
              <Button
                variant="default"
                size="sm"
                iconName="BarChart3"
                onClick={() => navigate('/studio-analytics')}
              >
                View Analytics
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Plays</p>
                  <p className="text-2xl font-bold text-foreground">
                    {dashboardData.analytics.totalPlays.toLocaleString()}
                  </p>
                  <p className="text-xs text-accent mt-1">
                    +{dashboardData.analytics.monthlyGrowth}% this month
                  </p>
                </div>
                <Icon name="Play" size={24} className="text-primary" />
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Monthly Revenue</p>
                  <p className="text-2xl font-bold text-foreground">
                    ${dashboardData.analytics.totalRevenue.toFixed(2)}
                  </p>
                  <p className="text-xs text-accent mt-1">
                    Revenue share: {studio?.revenue_share_percentage}%
                  </p>
                </div>
                <Icon name="DollarSign" size={24} className="text-accent" />
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Games</p>
                  <p className="text-2xl font-bold text-foreground">
                    {dashboardData.analytics.activeGames}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Published games
                  </p>
                </div>
                <Icon name="Gamepad2" size={24} className="text-secondary" />
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Games</p>
                  <p className="text-2xl font-bold text-foreground">
                    {dashboardData.games.length}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    In your portfolio
                  </p>
                </div>
                <Icon name="Code" size={24} className="text-primary" />
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Upload"
                  className="w-full justify-start"
                  onClick={() => navigate('/studio-games')}
                >
                  Upload New Game
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Key"
                  className="w-full justify-start"
                  onClick={() => navigate('/studio-api-keys')}
                >
                  Manage API Keys
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Settings"
                  className="w-full justify-start"
                  onClick={() => navigate('/studio-sdk')}
                >
                  SDK Integration
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="CreditCard"
                  className="w-full justify-start"
                  onClick={() => navigate('/studio-revenue')}
                >
                  View Payouts
                </Button>
              </div>
            </div>

            {/* Recent Games */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">Recent Games</h3>
              <div className="space-y-3">
                {dashboardData.games.slice(0, 4).map((game) => (
                  <div key={game.id} className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name="Gamepad2" size={16} className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {game.title}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {game.play_count} plays
                      </p>
                    </div>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${game.status === 'published' ? 'bg-green-100 text-green-800' :
                        game.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                      }`}>
                      {game.status}
                    </span>
                  </div>
                ))}
                {dashboardData.games.length === 0 && (
                  <div className="text-center py-4">
                    <Icon name="Gamepad2" size={32} className="text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No games yet</p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={() => navigate('/studio-games')}
                    >
                      Upload Your First Game
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">Recent Activity</h3>
              <div className="space-y-3">
                {dashboardData.recentActivity.map((activity, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                    <div className="flex-1">
                      <p className="text-sm text-foreground">{activity.message}</p>
                      <p className="text-xs text-muted-foreground">{activity.timestamp}</p>
                    </div>
                  </div>
                ))}
                {dashboardData.recentActivity.length === 0 && (
                  <div className="text-center py-4">
                    <Icon name="Activity" size={32} className="text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No recent activity</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* SDK Integration Status */}
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">SDK Integration Status</h3>
              <Button
                variant="outline"
                size="sm"
                iconName="ExternalLink"
                onClick={() => navigate('/studio-sdk')}
              >
                View Documentation
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-muted/30 rounded-lg">
                <Icon name="Shield" size={32} className="text-accent mx-auto mb-2" />
                <p className="text-sm font-medium text-foreground">Site Lock</p>
                <p className="text-xs text-muted-foreground">Protect your games</p>
              </div>
              <div className="text-center p-4 bg-muted/30 rounded-lg">
                <Icon name="BarChart3" size={32} className="text-primary mx-auto mb-2" />
                <p className="text-sm font-medium text-foreground">Analytics</p>
                <p className="text-xs text-muted-foreground">Track player behavior</p>
              </div>
              <div className="text-center p-4 bg-muted/30 rounded-lg">
                <Icon name="DollarSign" size={32} className="text-accent mx-auto mb-2" />
                <p className="text-sm font-medium text-foreground">Monetization</p>
                <p className="text-xs text-muted-foreground">Ads & IAP</p>
              </div>
              <div className="text-center p-4 bg-muted/30 rounded-lg">
                <Icon name="Cloud" size={32} className="text-secondary mx-auto mb-2" />
                <p className="text-sm font-medium text-foreground">Cloud Saves</p>
                <p className="text-xs text-muted-foreground">Cross-device progress</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default StudioDashboard;
